<template>
    <!-- Tag Add/Edit Modal -->
    <TagAddEditModal
        v-if="isAddEditModalOpen"
        :isOpen="isAddEditModalOpen"
        :tag="selectedTag"
        @update:isOpen="
            (event) => {
                isAddEditModalOpen = event;
            }
        "
        @save="handleSave" />

    <div class="flex flex-col h-[calc(100vh-1rem)] overflow-hidden">
        <div class="page-header">
            <AppHeader />
        </div>
        <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2 w-1/3">
                <SearchInput
                    :custom-class="'w-96'"
                    :loading="loaderStore.isLoading('tags.fetch')"
                    placeholder="Search by name, ID, type, or description"
                    @search="(input) => (searchQuery = input)" />

                <multiselect
                    class="w-2/3"
                    v-model="selectedFilterTypes"
                    :disabled="loaderStore.isLoading('tags.fetch')"
                    :options="TagTypeFilterOptions"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :select-label="'Select'"
                    :deselect-label="'Remove'"
                    placeholder="Filter by tag type"
                    label="name"
                    track-by="value">
                    <template #selection="{ values, isOpen }">
                        <span v-if="values.length" v-show="!isOpen" class="multiselect__single">
                            {{ values.length > 1 ? `${values.length} types selected` : values[0].name }}
                        </span>
                    </template>
                </multiselect>
            </div>
            <button
                v-can-access.permissions="[$Permissions.WRITE_TAGS]"
                aria-label="button"
                :disabled="loaderStore.isLoading('tags.fetch')"
                class="btn btn-primary rounded-lg flex items-center gap-1 focus:outline-hidden focus:ring-0 focus:ring-offset-0 bg-white border border-gray-300 p-2"
                @click="openAddModal">
                <span
                    aria-hidden="true"
                    class="inline-flex items-center rounded-lg border border-transparent bg-cyan-950 p-0.5 text-white shadow-xs hover:bg-cyan-900 mr-1">
                    <PlusIcon class="h-5 w-5" />
                </span>
                <span class="align-super font-medium text-sm">Add</span>
            </button>
        </div>
        <div class="flex-1 overflow-auto pb-3">
            <AppLoader :listen="['tags.fetch']" default-height="70vh">
                <ul role="list" class="bg-white shadow-xs ring-1 ring-gray-900/5 sm:rounded-xl overflow-visible">
                    <VirtList
                        itemKey="id"
                        :list="filteredTags"
                        :minSize="20"
                        class="overflow-visible"
                        style="overflow: visible !important"
                        ref="virtualList">
                        <template #default="{ itemData, index }" class="overflow-visible">
                            <li
                                class="relative flex justify-between gap-x-6 px-4 py-5 hover:bg-gray-50 sm:px-6"
                                :class="index < filteredTags.length - 1 ? 'border-b' : ''">
                                <div class="min-w-0">
                                    <div class="flex items-center gap-x-2">
                                        <p class="text-sm/6 font-semibold text-gray-900">{{ itemData.name }}</p>
                                        <span
                                            v-if="itemData.system"
                                            class="mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium text-red-700 bg-red-50 ring-1 ring-inset ring-red-600/20">
                                            System
                                        </span>
                                        <span
                                            v-else
                                            class="mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-600/20">
                                            Custom
                                        </span>
                                        <svg viewBox="0 0 2 2" class="size-0.5 fill-current">
                                            <circle cx="1" cy="1" r="1" />
                                        </svg>
                                        <span
                                            v-for="(t, index) in itemData.type"
                                            :key="index"
                                            :class="`mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium ${TAG_BADGE_COLOR_CLASSES_PER_TYPE[t]} ring-1 ring-inset ring-red-600/20`">
                                            {{ TagTypeToDisplayName[t] }}
                                        </span>
                                    </div>
                                    <div v-if="itemData.description" class="mt-1 flex items-center gap-x-2 text-xs/5 text-gray-500">
                                        <p class="truncate">{{ itemData.description }}</p>
                                    </div>
                                </div>
                                <!-- Action Dropdown (only for custom tags) -->
                                <div
                                    class="flex gap-2 items-center"
                                    v-if="!itemData.system"
                                    v-can-access.permissions.or="[$Permissions.WRITE_TAGS, $Permissions.DELETE_TAGS]">
                                    <HalfCircleSpinner v-if="actionLoading(itemData.id)" :loading="actionLoading(itemData.id)" />
                                    <div class="flex flex-none items-center gap-x-4">
                                        <Menu as="div" class="relative flex-none">
                                            <MenuButton
                                                class="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900"
                                                :disabled="actionLoading(itemData.id)">
                                                <span class="sr-only">Open options</span>
                                                <EllipsisVerticalIcon class="h-5 w-5" aria-hidden="true" />
                                            </MenuButton>
                                            <transition
                                                enter-active-class="transition ease-out duration-100"
                                                enter-from-class="transform opacity-0 scale-95"
                                                enter-to-class="transform opacity-100 scale-100"
                                                leave-active-class="transition ease-in duration-75"
                                                leave-from-class="transform opacity-100 scale-100"
                                                leave-to-class="transform opacity-0 scale-95">
                                                <MenuItems
                                                    :disabled="actionLoading(itemData.id)"
                                                    class="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden">
                                                    <MenuItem v-slot="{ active }" v-can-access.permissions="[$Permissions.WRITE_TAGS]">
                                                        <a
                                                            href="#"
                                                            @click.prevent="openEditModal(itemData)"
                                                            :class="[active ? 'bg-gray-50' : '', 'block px-3 py-1 text-sm text-gray-900']">
                                                            Edit
                                                        </a>
                                                    </MenuItem>
                                                    <MenuItem v-slot="{ active }" v-can-access.permissions="[$Permissions.DELETE_TAGS]">
                                                        <a
                                                            href="#"
                                                            @click.prevent="handleDeleteTag(itemData)"
                                                            :class="[active ? 'bg-gray-50' : '', 'block px-3 py-1 text-sm text-gray-900']">
                                                            Delete
                                                        </a>
                                                    </MenuItem>
                                                </MenuItems>
                                            </transition>
                                        </Menu>
                                    </div>
                                </div>
                                <div v-else>
                                    <span class="bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded-full">Read-Only</span>
                                </div>
                            </li>
                        </template>
                    </VirtList>
                </ul>
            </AppLoader>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Multiselect from 'vue-multiselect';
import { PlusIcon } from '@heroicons/vue/20/solid';
import { TagTypesInfo, TagTypeToDisplayName, TAG_BADGE_COLOR_CLASSES_PER_TYPE } from '@/utils/constants';
import useConfirmBeforeAction from '../../composables/useConfirmBeforeAction.js';
import TagAddEditModal from '@/components/Tags/TagAddEditModal.vue';
import AppHeader from '../../components/AppHeader.vue';
import { useTagsStore } from '@/stores/tags.store';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { EllipsisVerticalIcon } from '@heroicons/vue/20/solid';
import HalfCircleSpinner from '@/components/global/loader/HalfCircleSpinner.vue';
import { useLoaderStore } from '@/stores/loader.store.js';
import AppLoader from '@/components/AppLoader.vue';
import SearchInput from '@/components/global/SearchInput.vue';
import { VirtList } from 'vue-virt-list';

const isAddEditModalOpen = ref(false);
const selectedTag = ref(null);
const selectedFilterTypes = ref([]);
const tagsStore = useTagsStore();
const loaderStore = useLoaderStore();
const searchQuery = ref('');
const virtualList = ref(null);

onMounted(async () => {
    await tagsStore.fetch();
});

const actionLoading = computed(() => (tagId) => loaderStore.isLoading(`tags.delete.${tagId}` || loaderStore.isLoading(`tags.update.${tagId}`)));

const TagTypeFilterOptions = computed(() => TagTypesInfo.filter((type) => tagsStore.all.some((tag) => tag.type.includes(type.value))));

const filteredTags = computed(() => {
    if (!selectedFilterTypes.value.length && !searchQuery.value) {
        return tagsStore.all;
    }

    return tagsStore.all.filter((tag) => {
        const matchesType = selectedFilterTypes.value.length ? selectedFilterTypes.value.some((type) => tag.type.includes(type.value)) : true;

        const matchesSearch = searchQuery.value
            ? tag.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
              tag.id.toString().includes(searchQuery.value) ||
              tag.description.toLowerCase().includes(searchQuery.value.toLowerCase())
            : true;

        return matchesType && matchesSearch;
    });
});

watch(
    () => filteredTags.value,
    () => {
        virtualList.value?.forceUpdate();
    }
);

const openAddModal = () => {
    selectedTag.value = null;

    isAddEditModalOpen.value = true;
};

const openEditModal = (tag) => {
    selectedTag.value = tag;

    isAddEditModalOpen.value = true;
};

const handleSave = async (tagToSave) => {
    try {
        if (tagToSave.id) {
            // Update via API call in the store
            await tagsStore.updateTag(tagToSave);
        } else {
            // Add new tag via API call in the store
            await tagsStore.addTag(tagToSave);
        }

        isAddEditModalOpen.value = false;
        selectedTag.value = null;
    } catch (error) {
        console.error('Error saving/editing tag: ', error);
    }
};

const handleDeleteTag = async (tag) => {
    useConfirmBeforeAction(
        'delete',
        async () => {
            try {
                await tagsStore.deleteTag(tag.id);
            } catch (error) {
                console.error('Error deleting tag: ', error);
            }
        },
        { entity: 'the tag', name: tag.name },
        () => {}
    );
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
